import { useEffect, useState } from 'react'
import { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import Modal from '@/shared/components/Modal/Modal'
import Button from '@/shared/components/Button/Button'
import TextField from '@/shared/components/TextField/TextField'
import TimePicker from '@/shared/components/TimePicker/TimePicker'
import Select from '@/shared/components/Select/Select'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { Shift, ShiftDraft } from '../../types/schedule.model'
import {
  DEFAULT_SHIFT_TYPE,
  SHIFT_TYPES,
  ShiftType,
  isAllDayType
} from '../../types/shiftType.model'
import {
  SHIFT_TEMPLATES,
  TIME_STEP_MINUTES,
  dayjsToMinutes,
  MINUTES_IN_DAY,
  formatMinutes,
  minutesToDayjs,
  resolveEndMinutes
} from '../../utils/schedule'
import styles from './ShiftModal.module.scss'

interface ShiftModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (draft: ShiftDraft) => void
  onDelete?: (id: string) => void
  employees: Array<{ userId: string; name: string }>
  /** Pre-filled values, an existing shift when editing, day/user when adding */
  draft: ShiftDraft | null
  /**
   * The shift this one would double-book, if any. Supplied as a callback
   * rather than the roster itself, so the modal keeps knowing nothing about
   * where shifts are stored.
   */
  findConflict?: (draft: ShiftDraft) => Shift | undefined
}

function ShiftModal(props: ShiftModalProps) {
  const { isOpen, onClose, onSave, onDelete, employees, draft, findConflict } =
    props
  const { t } = useTranslation()

  const [userId, setUserId] = useState<OptionType | null>(null)
  const [type, setType] = useState<ShiftType>(DEFAULT_SHIFT_TYPE)
  const [start, setStart] = useState<Dayjs | null>(null)
  const [end, setEnd] = useState<Dayjs | null>(null)
  const [note, setNote] = useState('')
  // Errors sit on the field that caused them, not in a summary line at the
  // bottom, the message is next to the input you have to fix.
  const [errors, setErrors] = useState<{
    userId?: string
    start?: string
    end?: string
  }>({})

  const employeeOptions: OptionType[] = employees.map(employee => ({
    label: employee.name,
    value: employee.userId
  }))

  const typeOptions: OptionType[] = SHIFT_TYPES.map(definition => ({
    label: t(definition.labelKey),
    value: definition.type
  }))

  // An absence covers the day, so the clocks and the presets have nothing to
  // ask. The stored times stay 00:00-24:00, which is what every calendar and
  // the overlap check already understand.
  const isAllDay = isAllDayType(type)

  useEffect(() => {
    if (!isOpen || !draft) return

    setUserId(
      employeeOptions.find(option => option.value === draft.userId) ?? null
    )
    setType(draft.type ?? DEFAULT_SHIFT_TYPE)
    setStart(minutesToDayjs(draft.startMinutes))
    setEnd(minutesToDayjs(draft.endMinutes % (24 * 60)))
    setNote(draft.note ?? '')
    setErrors({})
    // Options are derived from props on every render; re-running on them would
    // reset the fields while the user types.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, draft])

  /*
    Switching an absence to a timed entry.

    An all-day entry is stored as 00:00-24:00, and the end picker is seeded with
    `endMinutes % 1440`, so both clocks read 00:00. That pair is zero length,
    which the validator rightly refuses, leaving the form stuck on "end must be
    later than start" with nothing on screen saying the way out is to click a
    preset. So the first preset is applied for them.

    Keyed on the clocks being equal rather than on the previous type: that is the
    condition that actually cannot be saved, and it leaves a genuine pair alone,
    so switching to an absence and back does not lose the hours that were there.
  */
  const handleTypeChange = (next: ShiftType) => {
    setType(next)
    setErrors(current => ({ ...current, start: undefined, end: undefined }))

    if (isAllDayType(next)) return

    const [preset] = SHIFT_TEMPLATES
    if (preset && dayjsToMinutes(start) === dayjsToMinutes(end)) {
      setStart(minutesToDayjs(preset.startMinutes))
      setEnd(minutesToDayjs(preset.endMinutes))
    }
  }

  const applyTemplate = (startMinutes: number, endMinutes: number) => {
    setStart(minutesToDayjs(startMinutes))
    setEnd(minutesToDayjs(endMinutes))
    setErrors(current => ({ ...current, start: undefined, end: undefined }))
  }

  const handleSave = () => {
    const startMinutes = isAllDay ? 0 : dayjsToMinutes(start)
    const endMinutes = isAllDay ? MINUTES_IN_DAY : dayjsToMinutes(end)

    // Everything is checked in one pass, so a form with two problems shows
    // both at once instead of revealing them one submit at a time.
    const nextErrors: typeof errors = {}

    if (!userId) {
      nextErrors.userId = t('SCHEDULE.VALIDATION.EMPLOYEE_REQUIRED')
    }
    if (startMinutes === null) {
      nextErrors.start = t('SCHEDULE.VALIDATION.TIME_FORMAT')
    }
    if (endMinutes === null) {
      nextErrors.end = t('SCHEDULE.VALIDATION.TIME_FORMAT')
    } else if (startMinutes !== null && endMinutes === startMinutes) {
      // An end BEFORE the start is a night shift, not a mistake, only an end
      // equal to the start describes nothing at all.
      nextErrors.end = t('SCHEDULE.VALIDATION.END_AFTER_START')
    }

    if (startMinutes === null || endMinutes === null || !userId) {
      setErrors(nextErrors)
      return
    }

    const saved: ShiftDraft = {
      id: draft?.id,
      userId: String(userId.value),
      date: draft?.date ?? '',
      startMinutes,
      endMinutes: isAllDay
        ? MINUTES_IN_DAY
        : resolveEndMinutes(startMinutes, endMinutes),
      type,
      note: note.trim() || undefined
    }

    // An overlap already in the data is not this edit's doing. Refusing it
    // would leave a shift caught in a pre-existing clash uneditable, its note
    // included, with no way to reach the very fields that would resolve it.
    // Only a change to the person or the hours has to answer for one.
    const rescheduled =
      !draft?.id ||
      saved.userId !== draft.userId ||
      saved.startMinutes !== draft.startMinutes ||
      saved.endMinutes !== draft.endMinutes ||
      saved.type !== draft.type

    const conflict = rescheduled ? findConflict?.(saved) : undefined

    if (conflict) {
      // On the end picker, because that is the field most edits move last and
      // the one the user can shorten to resolve it.
      nextErrors.end = t('SCHEDULE.VALIDATION.OVERLAPS', {
        from: formatMinutes(conflict.startMinutes),
        to: formatMinutes(conflict.endMinutes)
      })
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors)
      return
    }

    onSave(saved)
  }

  return (
    <Modal
      title={
        draft?.id
          ? t('SCHEDULE.MODAL.EDIT_TITLE')
          : t('SCHEDULE.MODAL.ADD_TITLE')
      }
      size="medium"
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className={styles.modalContent}>
        <p className={`body-medium ${styles.dateLabel}`}>{draft?.date}</p>

        <Select
          label={t('SCHEDULE.EMPLOYEE')}
          placeholder={t('SCHEDULE.MODAL.EMPLOYEE_PLACEHOLDER')}
          options={employeeOptions}
          value={userId}
          onChange={value => {
            setUserId(value as OptionType)
            setErrors(current => ({ ...current, userId: undefined }))
          }}
          hasError={!!errors.userId}
          helperText={errors.userId}
          focused
          required
        />

        <Select
          label={t('SCHEDULE.TYPE.LABEL')}
          placeholder={t('SCHEDULE.TYPE.LABEL')}
          options={typeOptions}
          value={typeOptions.find(option => option.value === type) ?? null}
          onChange={value =>
            handleTypeChange(
              ((value as OptionType)?.value as ShiftType) ?? DEFAULT_SHIFT_TYPE
            )
          }
          hideClearIcon
          focused
          required
        />

        {isAllDay ? (
          <p className={`body-medium ${styles.allDayNote}`}>
            {t('SCHEDULE.TYPE.ALL_DAY')}
          </p>
        ) : (
          <>
            <div className={styles.templateRow}>
              {SHIFT_TEMPLATES.map(template => (
                <Button
                  key={template.id}
                  label={t(template.labelKey)}
                  variant="outlined"
                  color="primary"
                  onClick={() =>
                    applyTemplate(template.startMinutes, template.endMinutes)
                  }
                />
              ))}
            </div>

            <div className={styles.timeRow}>
              <TimePicker
                label={t('SCHEDULE.MODAL.START')}
                value={start}
                onChange={value => {
                  setStart(value)
                  setErrors(current => ({ ...current, start: undefined }))
                }}
                minutesStep={TIME_STEP_MINUTES}
                hasError={!!errors.start}
                helperText={errors.start}
              />
              <TimePicker
                label={t('SCHEDULE.MODAL.END')}
                value={end}
                onChange={value => {
                  setEnd(value)
                  setErrors(current => ({ ...current, end: undefined }))
                }}
                minutesStep={TIME_STEP_MINUTES}
                hasError={!!errors.end}
                helperText={errors.end}
              />
            </div>
          </>
        )}

        <TextField
          label={t('SCHEDULE.MODAL.NOTE')}
          placeholder={t('SCHEDULE.MODAL.NOTE_PLACEHOLDER')}
          value={note}
          onChange={event => setNote(event.target.value)}
          focused
        />

        <div className={styles.buttonRow}>
          {draft?.id && onDelete && (
            <Button
              label={t('COMMON.DELETE')}
              variant="text"
              color="secondary"
              icon="las la-trash"
              onClick={() => onDelete(draft.id as string)}
            />
          )}
          <div className={styles.buttonRowEnd}>
            <Button
              label={t('COMMON.CANCEL')}
              variant="outlined"
              color="primary"
              onClick={onClose}
            />
            <Button
              label={t('COMMON.CONFIRM')}
              variant="contained"
              color="primary"
              onClick={handleSave}
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default ShiftModal
